/**
 * PrivateAgenda Component
 * 
 * Main landing page after authentication showing all wedding events.
 * Displays all events with scroll animations and navigation links.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../App.css';

// Centralized imports
import { useSuperAdminAuth, AuthGuard } from '../utils/auth';
import { useScrollWobble } from '../utils/scrollAnimations';
import { PAGE_EVENTS, VENUE_INFO } from '../data/eventsData';
import EventCard from '../components/EventCard';
import ClosingSection from '../components/shared/ClosingSection';

function PrivateAgenda() {
  // Use super admin authentication hook
  const { isValidAccess, isLoading } = useSuperAdminAuth('/prxivtaeagnd7283');
  
  // Use centralized scroll animation hook
  useScrollWobble();
  
  // State for bookmark bar visibility
  const [isBookmarkVisible, setIsBookmarkVisible] = useState(false);
  const [isBookmarkHidden, setIsBookmarkHidden] = useState(false);
  const [activeEvent, setActiveEvent] = useState('');
  const scrollTimeoutRef = useRef(null);
  
  // Function to set/reset the hide timeout
  const setHideTimeout = () => {
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Set new timeout to hide bookmark bar after 1.5 seconds of inactivity
    scrollTimeoutRef.current = setTimeout(() => {
      setIsBookmarkHidden(true);
    }, 1500);
  };
  
  // Handle scroll to show/hide bookmark bar
  useEffect(() => {
    const handleScroll = () => {
      const venueSection = document.querySelector('.location-section');
      if (venueSection) {
        const venueSectionBottom = venueSection.offsetTop + venueSection.offsetHeight;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        
        // Show bookmark bar after venue section
        const shouldShowBookmark = scrollTop > venueSectionBottom - 100;
        setIsBookmarkVisible(shouldShowBookmark);
        
        // Show bookmark bar on any scroll movement and set timeout
        if (shouldShowBookmark) {
          setIsBookmarkHidden(false);
          setHideTimeout();
        }
        
        // Determine active event based on scroll position
        const eventElements = document.querySelectorAll('[data-event-id]');
        let currentActiveEvent = '';
        
        eventElements.forEach((element) => {
          const rect = element.getBoundingClientRect();
          if (rect.top >= 0 && rect.top <= window.innerHeight / 2) {
            currentActiveEvent = element.getAttribute('data-event-id');
          }
        });
        
        setActiveEvent(currentActiveEvent);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);
  
  // Set initial timeout when bookmark becomes visible
  useEffect(() => {
    if (isBookmarkVisible && !isBookmarkHidden) {
      setHideTimeout();
    }
  }, [isBookmarkVisible, isBookmarkHidden]);
  
  // Function to scroll to specific event
  const scrollToEvent = (eventId) => {
    const element = document.querySelector(`[data-event-id="${eventId}"]`);
    if (element) {
      // Keep the bookmark bar visible during navigation
      setIsBookmarkHidden(false);
      
      // Clear any existing timeout to prevent immediate hiding
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Set a longer timeout after user interaction (5 seconds instead of 1.5)
      scrollTimeoutRef.current = setTimeout(() => {
        setIsBookmarkHidden(true);
      }, 5000);
    }
  };

  // Get all events from centralized data
  const events = PAGE_EVENTS.home();
  
  // Group events by date
  const eventsByDate = events.reduce((acc, event) => {
    const date = event.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(event);
    return acc;
  }, {});
  
  // Sort dates to ensure proper order
  const sortedDates = Object.keys(eventsByDate).sort((a, b) => {
    // Parse dates for proper sorting
    const dateA = new Date(a.replace(/(\d+)(st|nd|rd|th)/, '$1'));
    const dateB = new Date(b.replace(/(\d+)(st|nd|rd|th)/, '$1'));
    return dateA - dateB;
  });
  
  // Function to get appropriate header and ornament for each date
  const getDateHeaderInfo = (date) => {
    if (date === 'Monday, 24th November 2025') {
      return { title: 'Pre-Wedding Celebrations', ornament: '✨' };
    } else if (date === 'Tuesday, 25th Nov 2025') {
      return { title: 'Wedding Day & Reception', ornament: '💕' };
    } else {
      return { title: 'Wedding Events', ornament: '🌸' };
    }
  };

  return (
    <AuthGuard isLoading={isLoading} isValidAccess={isValidAccess}>
      <div className="app">
        {/* Hero Section */}
        <div className="hero">
          <div className="hero-text">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>warmly welcome you to their Wedding</p>
            <p>24th & 25th Nov 2025</p>
          </div>
        </div>

        {/* Hero Text Section for Mobile */}
        <div className="hero-text-section">
          <div className="hero-text-content">
            <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="hero-logo" />
            <h1>Radhika & Vivek</h1>
            <p>warmly welcome you to their Wedding</p>
            <p>24th & 25th Nov 2025</p>
          </div>
        </div>

        <div className="gradient-container">
          {/* Location Section */}
          <div className="location-section">
          <div className="location-container">
            <div className="location-title">
              <img src="/location.png" alt="Location" className="location-icon" />
              <h2>Wedding Venue</h2>
            </div>
            <div className="location-details">
              <h3>{VENUE_INFO.name}</h3>
              <div className="location-address">
                {VENUE_INFO.address.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Events Section */}
        <div className="agenda">
          {/* Events grouped by date */}
          {sortedDates.map((date, dateIndex) => {
            const headerInfo = getDateHeaderInfo(date);
            return (
              <div key={date}>
                {/* Date Header */}
                <div className="day-section">
                  <div className="day-header">
                    <h2>{headerInfo.title}</h2>
                    <div className="date-ornament">
                      <span className="ornament">{headerInfo.ornament}</span>
                      <span className="date">{date}</span>
                      <span className="ornament">{headerInfo.ornament}</span>
                    </div>
                  </div>
                </div>

                {/* Event Cards for this date */}
                {eventsByDate[date].map((event, eventIndex) => (
                  <div key={event.id} data-event-id={event.id}>
                    <EventCard 
                      event={event} 
                      isReversed={eventIndex % 2 === 1}
                    />
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Page Navigation */}
        <div className="page-navigation">
          <div className="page-nav-container">
            <h2 className="page-nav-title">Explore More</h2>
            <div className="page-nav-links">
              <Link to="/maagiennda" className="page-nav-link">
                <div className="nav-card">
                  <img src="/varmala.png" alt="Main Agenda" className="nav-card-image" />
                  <h3>Main Agenda</h3>
                  <p>Complete timeline of all wedding events</p>
                </div>
              </Link>
              <Link to="/cweerdedmionngy" className="page-nav-link">
                <div className="nav-card">
                  <img src="/wedding_pheras.png" alt="Wedding Day" className="nav-card-image" />
                  <h3>Wedding Day</h3>
                  <p>Sacred ceremonies and celebrations</p>
                </div>
              </Link>
              <Link to="/rfeucnecpttiioonn" className="page-nav-link">
                <div className="nav-card">
                  <img src="/reception.png" alt="Reception" className="nav-card-image" />
                  <h3>Reception</h3>
                  <p>Evening celebration and dinner</p>
                </div>
              </Link>
              <Link to="/swaendgdeientg" className="page-nav-link">
                <div className="nav-card">
                  <img src="/sangeet.png" alt="Sangeet & Wedding" className="nav-card-image" />
                  <h3>Sangeet & Wedding</h3>
                  <p>Music, dance, and sacred ceremonies</p>
                </div>
              </Link>
              <Link to="/wiendvdiitnagtion" className="page-nav-link">
                <div className="nav-card">
                  <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Invitation" className="nav-card-image" />
                  <h3>Invitation</h3>
                  <p>Formal wedding invitation</p>
                </div>
              </Link>
              <Link to="/crtlnk9472xwz" className="page-nav-link">
                <div className="nav-card">
                  <img src="/location.png" alt="Create Links" className="nav-card-image" />
                  <h3>Create Links</h3>
                  <p>Generate secure invitation links</p>
                </div>
              </Link>
              <Link to="/cmpctgnd5628vqr" className="page-nav-link">
                <div className="nav-card">
                  <img src="/varmala.png" alt="Compact Agenda" className="nav-card-image" />
                  <h3>Compact Agenda</h3>
                  <p>Condensed view of all events</p>
                </div>
              </Link>
              <Link to="/cmbndgnd8392plw" className="page-nav-link">
                <div className="nav-card">
                  <img src="/wedding_pheras.png" alt="Combined Agenda" className="nav-card-image" />
                  <h3>Combined Agenda</h3>
                  <p>Full agenda with quick timeline navigation</p>
                </div>
              </Link>
              <Link to="/sngwdrcp8472mjk" className="page-nav-link">
                <div className="nav-card">
                  <img src="/sangeet.png" alt="Sangeet Wedding Reception" className="nav-card-image" />
                  <h3>Main Events</h3>
                  <p>Sangeet, Wedding & Reception only</p>
                </div>
              </Link>
              <Link to="/tmlne4739hjk" className="page-nav-link">
                <div className="nav-card">
                  <img src="/wedding_pheras.png" alt="Timeline" className="nav-card-image" />
                  <h3>Timeline</h3>
                  <p>Timeline view of wedding schedule</p>
                </div>
              </Link>
              <Link to="/fllagnd8293kxm" className="page-nav-link">
                <div className="nav-card">
                  <img src="/varmala.png" alt="Full Agenda" className="nav-card-image" />
                  <h3>Full Agenda</h3>
                  <p>Detailed agenda with full event information</p>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Floating Bookmark Bar */}
        {isBookmarkVisible && (
          <div 
            className={`bookmark-bar ${isBookmarkHidden ? 'hidden' : ''}`}
            onMouseEnter={() => {
              // Keep bookmark visible when hovering
              setIsBookmarkHidden(false);
              if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
              }
            }}
            onMouseLeave={() => {
              // Set timeout to hide when mouse leaves
              setHideTimeout();
            }}
          >
            <div className="bookmark-header">
              <h3>Events</h3>
              <span className="bookmark-ornament">✨</span>
            </div>
            <div className="bookmark-items">
              {events.map((event) => (
                <button
                  key={event.id}
                  className={`bookmark-item ${activeEvent === event.id ? 'active' : ''}`}
                  onClick={() => scrollToEvent(event.id)}
                  title={`${event.title} - ${event.time}`}
                >
                  <span className="bookmark-title">{event.title}</span>
                  <span className="bookmark-time">{event.time}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
          {/* Closing Section */}
          <ClosingSection variant="simple" />
        </div>
      </div>
    </AuthGuard>
  );
}

export default PrivateAgenda;